import { zodResolver } from '@hookform/resolvers/zod'
import { Box } from '@mui/material'
import { useEffect } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { useNotificationStack } from '../../../app/hooks/useNotificationStack'
import {
    useCreateCustomerBrandMutation,
    useGetCustomerBrandByIdQuery,
    useUpdateCustomerBrandMutation
} from '../../../app/redux-fetch/apiProducts'
import SubmitButtons from '../../../components/Buttons/SubmitButtons'
import FormInput from '../../../components/Forms/FormInput'
import SpinnerModalBlock from '../../../components/Spinner/SpinnerModalBlock'
import { useSideModalContext } from '../../Assets/modals/SideModalContext'
import { messages } from '../messages'
import { BrandFormData, getBrandSchema } from '../schema'
import { Brand } from '../types'

type TenantBrandFormProps = {
    brandId?: number
    customerId: number
    isDisabled?: boolean
}

const TenantBrandForm = ({ brandId = 0, customerId, isDisabled = false }: TenantBrandFormProps) => {
    const { formatMessage } = useIntl()
    const isNew = !brandId || brandId === 0
    const { onClose } = useSideModalContext()

    const [createCustomerBrand] = useCreateCustomerBrandMutation()
    const [updateCustomerBrand] = useUpdateCustomerBrandMutation()
    const { enqueueSuccess, enqueueFailure } = useNotificationStack()

    const { data: existingBrand, isLoading: isLoadingBrand } = useGetCustomerBrandByIdQuery(
        { customerId, id: brandId },
        { skip: isNew }
    )

    const defaultBrand: Brand = {
        id: brandId,
        name: '',
        tenantId: 0,
        systemDefined: false,
        inUse: false
    }

    const methods = useForm<BrandFormData>({
        resolver: zodResolver(getBrandSchema(formatMessage)),
        defaultValues: defaultBrand,
    })

    const {
        handleSubmit,
        reset,
    } = methods

    useEffect(() => {
        if (!isNew && existingBrand) {
            reset(existingBrand)
        }
    }, [existingBrand, isNew, reset])

    const onSubmit = async (data: BrandFormData) => {
        if (isNew) {
            await createCustomerBrand({ customerId, body: { ...data, customerId } })
                .then(() => {
                    enqueueSuccess(messages.brandCreated)
                    onClose()
                })
                .catch(() => {
                    enqueueFailure(messages.brandNotAdded)
                })
        } else {
            await updateCustomerBrand({ customerId, body: { ...existingBrand, ...data } })
                .then(() => {
                    enqueueSuccess(messages.brandUpdated)
                    onClose()
                })
                .catch(() => enqueueFailure(messages.brandNotUpdated))
        }
    }

    const onSubmitHandler = handleSubmit(onSubmit)

    if (isLoadingBrand) {
        return <SpinnerModalBlock />
    }

    return (
        <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit)}>
                <Box sx={{ minWidth: '450px', m: '32px', pt: '48px' }}>
                    <FormInput
                        name="name"
                        label={formatMessage(messages.name)}
                        required
                        disabled={isDisabled}
                        maxLength={250}
                    />
                </Box>
                <Box
                    sx={{
                        position: 'fixed',
                        bottom: 0,
                        width: '100%',
                        padding: '16px',
                        backgroundColor: '#fff',
                        borderTop: '1px solid #e0e0e0',
                        zIndex: 999,
                    }}
                >
                    {!isDisabled && (
                        <SubmitButtons
                            create={isNew}
                            isEnabled={true}
                            submitForm={onSubmitHandler}
                        />
                    )}
                </Box>
            </form>
        </FormProvider>
    )
}

export default TenantBrandForm
